import cart from '../../cart/scripts/cart.js';

class SlideInCart {
  constructor(root) {
    this.root = root;
    this.busy = false;
    this.undo = null;
  }

  init() {
    document.addEventListener('click', event => {
      const plus = event.target.closest('[data-sic-plus]');
      const minus = event.target.closest('[data-sic-minus]');
      const remove = event.target.closest('[data-sic-remove]');
      const undoButton = event.target.closest('[data-sic-undo]');
      const retry = event.target.closest('[data-sic-retry]');

      if (plus || minus) this.step(event.target.closest('[data-sic-item]'), plus ? 1 : -1);
      else if (remove) this.remove(event.target.closest('[data-sic-item]'));
      else if (undoButton) this.restore();
      else if (retry) this.retry();
    });

    this.watchOpen();
  }

  // The footer's Trustpilot widget lives inside a <dialog> that stays hidden
  // until the drawer opens, so the Trustpilot bootstrap can't render it on page
  // load (and refresh() re-renders it). Re-initialise it whenever the drawer
  // opens — the same widget that already renders on the checkout.
  watchOpen() {
    const dialog = this.root.querySelector('.cart-drawer__dialog');
    if (!dialog) return;
    new MutationObserver(() => {
      if (dialog.open) setTimeout(() => this.initTrustpilot(), 0);
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  initTrustpilot() {
    const widgets = this.root.querySelectorAll('.trustpilot-widget');
    if (!widgets.length) return;
    const load = () => {
      if (!window.Trustpilot) return false;
      widgets.forEach(el => window.Trustpilot.loadFromElement(el, true));
      return true;
    };
    if (load()) return;
    // Bootstrap script may still be loading — retry briefly.
    let tries = 0;
    const timer = setInterval(() => {
      if (load() || (tries += 1) > 30) clearInterval(timer);
    }, 200);
  }

  setLoading(on) {
    this.root.querySelector('.slide-cart__items')?.classList.toggle('is-loading', on);
  }

  async step(item, delta) {
    if (!item || this.busy) return;
    const quantity = parseInt(item.dataset.lineQty, 10) + delta;
    if (quantity < 1) return;

    const qtyEl = item.querySelector('[data-sic-qty]');
    if (qtyEl) qtyEl.textContent = quantity;

    this.busy = true;
    this.setLoading(true);
    try {
      const response = await cart.changeItem(item.dataset.lineKey, quantity);
      if (!response.ok) throw new Error('cart change failed');
      const data = await response.json().catch(() => null);
      this.updateBubbles(data);
      await this.refresh();
    } catch (error) {
      this.showError();
    } finally {
      this.setLoading(false);
      this.busy = false;
    }
  }

  async remove(item) {
    if (!item || this.busy) return;
    const removed = {
      variantId: Number(item.dataset.variantId),
      quantity: parseInt(item.dataset.lineQty, 10),
      title: item.dataset.productTitle,
    };

    this.busy = true;
    this.setLoading(true);
    try {
      const response = await cart.changeItem(item.dataset.lineKey, 0);
      if (!response.ok) throw new Error('cart change failed');
      const data = await response.json().catch(() => null);
      this.updateBubbles(data);
      await this.refresh();
      this.showUndo(removed);
    } catch (error) {
      this.showError();
    } finally {
      this.setLoading(false);
      this.busy = false;
    }
  }

  showUndo(removed) {
    this.clearUndo();

    const toast = document.createElement('div');
    toast.className = 'slide-cart-toast';
    toast.setAttribute('role', 'status');

    const icon = document.createElement('span');
    icon.className = 'slide-cart-toast__icon';
    icon.innerHTML = '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M16.7 5a1 1 0 0 1 .08 1.41l-7.07 8a1 1 0 0 1-1.46.05l-3.93-3.84a1 1 0 1 1 1.4-1.43l3.18 3.1 6.39-7.22A1 1 0 0 1 16.7 5Z" fill="currentColor"/></svg>';

    const message = document.createElement('span');
    message.className = 'slide-cart-toast__message';
    message.textContent = `${removed.title} ${this.root.dataset.sicRemovedText}`;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'slide-cart-toast__undo';
    button.setAttribute('data-sic-undo', '');
    button.textContent = this.root.dataset.sicUndoText;

    toast.append(icon, message, button);
    const dialog = this.root.querySelector('.cart-drawer__dialog');
    if (dialog) dialog.appendChild(toast);

    this.undo = {
      ...removed,
      toast,
      timer: setTimeout(() => this.clearUndo(), 5000),
    };
  }

  async restore() {
    const undo = this.undo;
    if (!undo) return;
    this.clearUndo();

    this.busy = true;
    this.setLoading(true);
    try {
      const response = await cart.add(undo.variantId, undo.quantity);
      if (!response.ok) throw new Error('cart add failed');
      const data = await cart.get();
      this.updateBubbles(data);
      await this.refresh();
    } catch (error) {
      this.showError();
    } finally {
      this.setLoading(false);
      this.busy = false;
    }
  }

  async retry() {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.refresh();
    } catch (error) {
      this.showError();
    } finally {
      this.busy = false;
    }
  }

  clearUndo() {
    if (!this.undo) return;
    clearTimeout(this.undo.timer);
    this.undo.toast.remove();
    this.undo = null;
  }

  showError() {
    const template = this.root.querySelector('[data-sic-error-template]');
    const content = this.root.querySelector('.cart-drawer__content');
    if (!template || !content) return;
    content.replaceChildren(template.content.cloneNode(true));
  }

  updateBubbles(data) {
    if (!data || typeof data.item_count !== 'number') return;
    const count = data.item_count > 99 ? '99+' : String(data.item_count);
    document.querySelectorAll('.cart-bubble').forEach(bubble => {
      bubble.classList.toggle('visually-hidden', data.item_count === 0);
      const text = bubble.querySelector('.cart-bubble__text');
      if (text) text.textContent = count;
    });
  }

  async refresh() {
    const host = this.root.querySelector('cart-items-component[data-section-id]');
    const sectionId = host?.dataset.sectionId;
    if (!sectionId) return;

    const response = await window.fetch(`${window.location.pathname}?sections=${sectionId}`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('section render failed');

    const sections = await response.json().catch(() => null);
    const html = sections?.[sectionId];
    if (!html) throw new Error('section missing in render');

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-items-component');
    if (fresh) host.innerHTML = fresh.innerHTML;
    setTimeout(() => this.initTrustpilot(), 0);
  }
}

function bootSlideInCart() {
  const root = document.querySelector('cart-drawer-component');
  if (!root) return;
  const slideInCart = new SlideInCart(root);
  slideInCart.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSlideInCart);
} else {
  bootSlideInCart();
}

export default SlideInCart;
